import { DataType, StyleLayers } from '../types/lib/types';
import type * as maplibreglType from 'maplibre-gl';
import { DEFAULT_COLOR, DEFAULT_TEXT_COLOR, DEFAULT_CIRCLE_RADIUS, DEFAULT_FILL_OPACITY, DEFAULT_OUTLINE_WIDTH, DEFAULT_OUTLINE_OPACITY, DEFAULT_TEXT_HALO_COLOR, DEFAULT_CIRCLE_OPACITY, DEFAULT_STROKE_WIDTH } from '../constants/mapStyle';
import { ALERT_TILE_SOURCE_LAYER } from './getFeatures';
import { anyType } from '../types';
import { Feature } from 'geojson';
import { MapStyleSourcesObjType } from '../types/components/mapStyleConfig';

export const LAYER_GEOMETRY_TYPES = {
  point: 'Point',
  line: 'LineString',
  polygon: 'Polygon',
  polygonOutline: 'Polygon-outline',
  label: 'Label',
  symbol: 'Symbol',
  raster: 'Raster',
  alert: 'Alert'
};

/**
 * 定義されていないレイヤー用のスタイル（点、線、面）
 */
export const defaultStyle = (menuItem: DataType | MapStyleSourcesObjType, sourceId: string) => {

  const layerId = menuItem.id;
  let sourceLayerId: string | undefined = menuItem.tileId;
  let fillRaw = 'metadata' in menuItem ? menuItem.metadata?.fill : undefined;
  let strokeRaw = 'metadata' in menuItem ? menuItem.metadata?.stroke : undefined;
  let strokeWidthRaw = 'metadata' in menuItem ? menuItem.metadata?.['stroke-width'] : undefined;
  const markerSymbolRaw = 'metadata' in menuItem ? menuItem.metadata?.['marker-symbol'] : undefined;
  let markerColorRaw = 'metadata' in menuItem ? menuItem.metadata?.['marker-color'] : undefined;
  const fillPatternRaw = 'metadata' in menuItem ? menuItem.metadata?.['fill-pattern'] : undefined;

  if('layerStyle' in menuItem) {
    fillRaw = (menuItem as MapStyleSourcesObjType).layerStyle.fill;
    strokeRaw = (menuItem as MapStyleSourcesObjType).layerStyle.stroke;
    strokeWidthRaw = (menuItem as MapStyleSourcesObjType).layerStyle['stroke-width'];
    markerColorRaw = (menuItem as MapStyleSourcesObjType).layerStyle['marker-color'];
  }

  if('dataType' in menuItem && menuItem.dataType === 'fiware' && !('tileId' in menuItem)) {
    sourceLayerId = undefined;
  } else if('dataType' in menuItem && menuItem.dataType === 'fiware'){
    sourceLayerId = ALERT_TILE_SOURCE_LAYER;
  }

  const fillColor: maplibreglType.DataDrivenPropertyValueSpecification<string> = [
    'case',
    ['has', 'fill'],
    ['get', 'fill'],
    fillRaw ?? DEFAULT_COLOR
  ];

  const outlineWidth: maplibreglType.DataDrivenPropertyValueSpecification<number> = [
    'case',
    ['has', 'stroke-width'],
    ['get', 'stroke-width'],
    strokeWidthRaw ?? DEFAULT_OUTLINE_WIDTH
  ];

  const fillOpacity: maplibreglType.DataDrivenPropertyValueSpecification<number> = [
    'case',
    ['has', 'fill-opacity'],
    ['get', 'fill-opacity'],
    DEFAULT_FILL_OPACITY
  ];

  const strokeColor: maplibreglType.DataDrivenPropertyValueSpecification<string> = [
    'case',
    ['has', 'stroke'],
    ['get', 'stroke'],
    strokeRaw ?? fillRaw ?? DEFAULT_COLOR
  ];

  const strokeOpacity: maplibreglType.DataDrivenPropertyValueSpecification<number> = [
    'case',
    ['has', 'stroke-opacity'],
    ['get', 'stroke-opacity'],
    DEFAULT_OUTLINE_OPACITY
  ];

  // FIXME: stroke-width が GeoJSONにある場合に、GeoJSONの値を優先するようにしたい（coalesceやcaseを試してみたがワークしなかった）
  const strokeWidth: maplibreglType.DataDrivenPropertyValueSpecification<number> = strokeWidthRaw ?? DEFAULT_STROKE_WIDTH;

  const markerColor: maplibreglType.DataDrivenPropertyValueSpecification<string> = [
    'case',
    ['has', 'marker-color'],
    ['get', 'marker-color'],
    markerColorRaw ?? DEFAULT_COLOR
  ];

  const markerSize: maplibreglType.DataDrivenPropertyValueSpecification<number> = [
    'case',
    ['has', 'marker-size'],
    ['match',
      ['get', 'marker-size'],
      'small', 0.3,
      'large', 0.7,
      0.5
    ],
    0.5
  ];

  const circleRadius: maplibreglType.DataDrivenPropertyValueSpecification<number> = [
    'case',
    ['==', 'small', ['get', 'marker-size']], 7,
    ['==', 'large', ['get', 'marker-size']], 13,
    DEFAULT_CIRCLE_RADIUS
  ]

  const textColor: maplibreglType.DataDrivenPropertyValueSpecification<string> = [
    'string',
    ['get', 'text-color'],
    DEFAULT_TEXT_COLOR
  ];

  const textHaloColor: maplibreglType.DataDrivenPropertyValueSpecification<string> = [
    'string',
    ['get', 'text-halo-color'],
    DEFAULT_TEXT_HALO_COLOR
  ];

  const fill = fillPatternRaw ? { 'fill-pattern': fillPatternRaw } : { 'fill-color': fillColor };

  const layers: StyleLayers = [
    // ------------ 面 ---------------
    {
      id: `${layerId}-${LAYER_GEOMETRY_TYPES.polygon}`,
      type: 'fill',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        ...fill,
        'fill-opacity': fillOpacity,
      },
      layout: {
        'visibility': 'none'
      }
    },
    {
      id: `${layerId}-${LAYER_GEOMETRY_TYPES.polygonOutline}`,
      type: 'line',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: {
        'line-color': strokeColor,
        'line-width': outlineWidth,
        'line-opacity': strokeOpacity
      },
      layout: {
        'visibility': 'none'
      }
    },
    // ------------ 線 ---------------
    {
      id: `${layerId}-${LAYER_GEOMETRY_TYPES.line}`,
      type: 'line',
      source: sourceId,
      filter: ['==', ['geometry-type'], 'LineString'],
      paint: {
        'line-color': strokeColor,
        'line-width': strokeWidth,
        'line-opacity': strokeOpacity
      },
      layout: {
        'visibility': 'none'
      }
    },
    // ------------ 点 ---------------
    {
      id: `${layerId}-${LAYER_GEOMETRY_TYPES.point}`,
      type: 'circle',
      source: sourceId,
      filter: [
        'all',
        ['==', ['geometry-type'], 'Point'],
        ['!', ['any', ['has', 'marker-symbol']] ]
      ],
      paint: {
        'circle-radius': circleRadius,
        'circle-color': markerColor,
        'circle-opacity': [
          'case',
          ['has', 'marker-opacity'],
          ['get', 'marker-opacity'],
          DEFAULT_CIRCLE_OPACITY
        ],
        'circle-stroke-width': outlineWidth,
        'circle-stroke-color': strokeColor,
        'circle-stroke-opacity': strokeOpacity
      },
      layout: {
        'visibility': 'none'
      }
    },
    {
      id: `${layerId}-${LAYER_GEOMETRY_TYPES.symbol}`,
      type: 'symbol',
      source: sourceId,
      filter: [
        'all',
        ['==', ['geometry-type'], 'Point'],
      ],
      layout: {
        // icon-imageは 後続の処理で動的に設定
        'icon-size': markerSize,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-anchor': 'bottom',
        'icon-rotate': ['get', '_rotate'],
        'visibility': 'none'
      },
      paint: {
        'icon-color': markerColor,
        'icon-opacity': [
          'case',
          ['has', 'marker-opacity'],
          ['get', 'marker-opacity'],
          1
        ]
      }
    }
  ]

  // ------------ 動的にアイコンと丸を出し分ける処理 ---------------
  // Excelで marker-symbol が指定されている場合は、その値を使い、なければ、GeoJSONのプロパティを使う
  // FIX: GeoJSONのプロパティを優先するように修正したい
  const symbolLayer = layers.find(layer => layer.type === 'symbol');
  const circleLayer = layers.find(layer => layer.type === 'circle');
  if (symbolLayer && symbolLayer.layout) {
    if (markerSymbolRaw) {
      symbolLayer.layout['icon-image'] = `${markerSymbolRaw}`;
      if (circleLayer) {
        const index = layers.indexOf(circleLayer);
        if (index > -1) {
          layers.splice(index, 1);
        }
      }
    } else {
      symbolLayer.layout['icon-image'] = ['get', 'marker-symbol'];
    }
  }

  // ------------ ラベル ---------------
  layers.push({
    id: `${layerId}-${LAYER_GEOMETRY_TYPES.label}`,
    type: 'symbol',
    source: sourceId,
    filter: ['==', ['geometry-type'], 'Point'],
    layout: {
      'text-field': ['get', 'title'],
      'text-size': 12,
      // FIXME: 地理院のフォントが 'NotoSansJP-Regular'で、Geoloniaのフォントが 'Noto Sans Regular' なので、
      // どちらも使えるようにしたい。
      // https://maplibre.org/maplibre-style-spec/expressions/#literal がワークしなかった。
      'text-font': ['NotoSansJP-Regular'],
      'text-anchor': 'center',
      'text-padding': 2,
      'text-offset': [0, 1],
      'visibility': 'none'
    },
    paint: {
      'text-color': textColor,
      'text-halo-width': 2.5,
      'text-halo-color': textHaloColor
    }
  });

  if(sourceLayerId && sourceLayerId !== '') {
    layers.forEach(layer => {
      layer['source-layer'] = sourceLayerId;
    });
  }

  return layers;
}

/* ************************
 * レイヤーを追加
 * ************************/
export const generateLayers = (map: maplibreglType.Map | null, menuItem: DataType, sourceId: string): maplibregl.LayerSpecification[] | null => {

  if (!map || map.getLayer(menuItem.id)) { return null; }

  /** データPNG or ラスターの場合 **/
  if (
    menuItem.metadata?.dataType === 'datapng' 
    || menuItem.metadata?.dataType === 'raster'
  ) {
    const opacity = menuItem.metadata?.dataType === 'datapng' ? 0.5 : 1;
    return [
      {
        id: `${menuItem.id}-${LAYER_GEOMETRY_TYPES.raster}`,
        type: 'raster',
        source: menuItem.tileId,
        paint: {
          'raster-opacity': opacity
        },
        layout: {
          'visibility': 'none'
        }
      }
    ];
  }
  
  // 決まったスタイルがない場合
  const style = defaultStyle(
    menuItem,
    sourceId
  );
  return style;
};

/* ************************
 * fiwareデータのフィルターを生成
 * ************************/
export const generateFiwarePolygonFilter = (fiwareData: { [key: string]: anyType; }, dataId: string) => {

  if(!fiwareData || !fiwareData[dataId]) {
      if(dataId.includes('EvacuationInformation')) {
          return { fillColorExpression: '#00000000', filter: ['in', ['get', 'id'], ['literal', []]] }
      }
      return undefined;
  }

  const fillValueMap: { [key: string]: string[] } = {};
  const fiwareItemData = fiwareData[dataId];

  if (fiwareItemData && fiwareItemData.features) {
      fiwareItemData.features.forEach((feature: Feature) => {
          const fillValue = feature.properties?.fill;
          if (fillValue) {
              if (!fillValueMap[fillValue]) { fillValueMap[fillValue] = []; }
              fillValueMap[fillValue].push((feature.properties?.ID??0) as string);
          }
      });
  }

  let fillColorExpression: anyType = ['case'];
  const fillValues: string[] = [];

  Object.keys(fillValueMap).forEach(fillValue => {
    const ids: string[] = fillValueMap[fillValue];
    fillValues.push(...ids);
    fillColorExpression.push(['in', ['get', 'id'], ['literal', ids]], fillValue);
  });

  if(fillColorExpression.length === 1) { 
    fillColorExpression = '#00000000'; 
  } else {
    fillColorExpression.push('#00000000'); 
  }
  
  return { fillColorExpression, filter: ['in', ['get', 'id'], ['literal', fillValues]] };
}

/* ************************
 * fiwareデータのスタイルを更新
 * ************************/
export const updateFiwarePolygonLayer = (map: maplibregl.Map | null, layerId: string, colorFilter: anyType, filter: anyType) => {
  if(!map) { return; }
  
  const newFilter: maplibregl.FilterSpecification = ['all', ['==', ['geometry-type'], 'Polygon'], filter];
  map.setFilter(`${layerId}-${LAYER_GEOMETRY_TYPES.polygon}`, newFilter);
  map.setFilter(`${layerId}-${LAYER_GEOMETRY_TYPES.polygonOutline}`, newFilter);
  map.setPaintProperty(`${layerId}-${LAYER_GEOMETRY_TYPES.polygon}`, 'fill-color', colorFilter);
  map.setPaintProperty(`${layerId}-${LAYER_GEOMETRY_TYPES.polygonOutline}`, 'line-color', colorFilter);
};
